import axios from 'axios';
import { AUTH_API_URL } from '../config/env';
import {
  getAccessToken, getCsrfToken,
  setAccessToken, clearAccessToken,
  getRefreshingPromise, setRefreshingPromise, clearRefreshingPromise,
} from './tokenStore';

/**
 * authAxios — used for auth-service calls (login, refresh, register, etc.)
 * Note: The login/refresh endpoints themselves don't need an access token,
 * but authenticated auth-service calls (e.g. /users/me, /logout) do.
 */
const authAxios = axios.create({
  withCredentials: true,
  baseURL: AUTH_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

authAxios.interceptors.request.use(
  (config) => {
    const csrf = getCsrfToken();
    if (csrf) config.headers['x-csrf-token'] = csrf;

    const token = getAccessToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;

    return config;
  },
  (error) => Promise.reject(error)
);

authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status !== 401 ||
      original._retried ||
      original.url?.includes('/login') ||
      original.url?.includes('/refresh') ||
      original.url?.includes('/password-reset')
    ) {
      return Promise.reject(error);
    }
    original._retried = true;

    if (!getRefreshingPromise()) {
      const refreshPromise = (async () => {
        try {
          const csrf = getCsrfToken();
          const res = await axios.post(
            `${AUTH_API_URL}/refresh`,
            {},
            { withCredentials: true, headers: { ...(csrf ? { 'x-csrf-token': csrf } : {}) } }
          );
          setAccessToken(res.data.data.accessToken);
        } catch {
          clearAccessToken();
          localStorage.removeItem('isAuthenticated');
          if (window.location.pathname !== '/login' && !window.location.pathname.includes('/auth/magic')) {
            window.location.href = '/login';
          }
          throw error;
        } finally {
          clearRefreshingPromise();
        }
      })();
      setRefreshingPromise(refreshPromise);
    }

    await getRefreshingPromise();
    original.headers['Authorization'] = `Bearer ${getAccessToken()}`;
    return authAxios(original);
  }
);

export default authAxios;
